import React, { useState } from "react";
import { AGE_GROUPS, useAgeGroup } from "@/lib/ageGroup";
import { cn } from "@/lib/utils";

/**
 * AgeBandPicker — "Viewing for 7–10 · Change Age". A VIEW preference (device-local), never an account or a lock.
 * Selected state is shown with a ✓ + aria-checked, not colour alone.
 */
export default function AgeBandPicker({ testid = "age-band" }) {
  const [id, setAge, meta] = useAgeGroup();
  const [editing, setEditing] = useState(false);
  const open = editing || !id;
  const pick = (g) => { setAge(g.id); setEditing(false); };
  return (
    <div className="mwq-card p-3" data-testid={`${testid}-picker`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-extrabold text-[var(--color-text)]" data-testid={`${testid}-status`} aria-live="polite">
          {meta ? <><span aria-hidden>{meta.emoji} </span>Viewing for {meta.band}</> : <><span aria-hidden>👀 </span>Viewing for all ages</>}
        </p>
        {id && (
          <button type="button" onClick={() => setEditing((v) => !v)} aria-expanded={open} data-testid={`${testid}-change`}
            className="min-h-[44px] px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-sm">
            {open ? "Done" : "Change Age"}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2">
          <p className="mwq-meta mb-2">{id ? "Pick what fits you best." : "Make MWQ fit you — pick an age group. Not sure? You can change this anytime."}</p>
          <div role="radiogroup" aria-label="Age group" className="grid grid-cols-3 gap-2">
            {AGE_GROUPS.map((g) => {
              const on = g.id === id;
              return (
                <button key={g.id} type="button" role="radio" aria-checked={on} onClick={() => pick(g)} data-testid={`${testid}-pick-${g.id}`}
                  className={cn("min-h-[64px] rounded-2xl border-[3px] border-slate-900 px-2 py-2 flex flex-col items-center justify-center gap-0.5 font-extrabold text-sm focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/70",
                    on ? "bg-slate-900 text-white" : "bg-white hover:bg-yellow-100")}>
                  <span aria-hidden className="text-2xl leading-none">{g.emoji}</span>
                  <span>{g.band}{on ? " ✓" : ""}</span>
                </button>
              );
            })}
          </div>
          {id && (
            <button type="button" onClick={() => { setAge(null); setEditing(false); }} data-testid={`${testid}-clear`}
              className="mt-2 min-h-[44px] w-full rounded-full border-2 border-dashed border-slate-500 bg-white font-bold text-sm text-slate-700">
              Show everything for all ages
            </button>
          )}
        </div>
      )}
    </div>
  );
}
